import { ArrowUpRight, Search } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { defaultAdminNavigation, type AdminNavigationGroup } from "../../app/navigation";
import { Dialog } from "./Dialog";
import { Input } from "./FormControls";
import { cn } from "./cn";

export function NavigationSearch({ navigation = defaultAdminNavigation, large = false, shortcut = false }: {
  navigation?: AdminNavigationGroup[];
  large?: boolean;
  shortcut?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  function changeOpen(value: boolean) {
    setOpen(value);
    if (!value) setQuery("");
  }
  useEffect(() => {
    if (!shortcut) return;
    const listener = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        setOpen((value) => !value);
        setQuery("");
      }
    };
    window.addEventListener("keydown", listener);
    return () => window.removeEventListener("keydown", listener);
  }, [shortcut]);
  const normalize = (value: string) => value.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
  const results = navigation.flatMap((group) => group.items).filter((item) =>
    normalize(item.label + " " + (item.description ?? "")).includes(normalize(query.trim()))
  );
  return (
    <>
      <button type="button" onClick={() => changeOpen(true)}
        className={cn("flex w-full items-center gap-2.5 rounded-md border border-input bg-card px-3 text-left text-sm text-muted-foreground transition-colors hover:border-zinc-400",
          large ? "h-12 px-4 shadow-xs" : "h-9 text-xs")}
        aria-label="Buscar áreas do painel">
        <Search className="size-4 shrink-0" aria-hidden="true" />
        <span className="flex-1">{large ? "O que você quer gerenciar?" : "Buscar no painel…"}</span>
        <kbd className="hidden rounded border bg-background px-1.5 py-0.5 font-sans text-[10px] sm:inline-block">Ctrl K</kbd>
      </button>
      <Dialog open={open} onOpenChange={changeOpen} title="Buscar no painel" description="Encontre uma área administrativa." size="md" initialFocusRef={inputRef}>
        <Input ref={inputRef} aria-label="Nome da área" placeholder="Jogadores, avatares, auditoria…" value={query} onChange={(event) => setQuery(event.target.value)} />
        <div className="mt-3 space-y-1" aria-live="polite">
          {results.length ? results.map((item) => (
            <Link key={item.to} to={item.to} onClick={() => changeOpen(false)} className="flex items-center gap-3 rounded-md px-3 py-3 text-sm hover:bg-muted focus-visible:bg-muted">
              <item.icon className="size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
              <span className="flex-1">{item.label}</span>
              <ArrowUpRight className="size-3.5 text-muted-foreground" aria-hidden="true" />
            </Link>
          )) : <p className="px-3 py-8 text-center text-sm text-muted-foreground">Nenhuma área encontrada. Tente outro termo.</p>}
        </div>
      </Dialog>
    </>
  );
}
